import type { AccountInventoryError, AccountsBanner } from './accounts.model';

const DESIGN_BRIGHTSIDE_SHORT_NAMES = ['Spending', 'Savings', 'Cash Advance'] as const;
const TYPE_ORDER = ['Checking', 'Savings', 'Debt', 'Earned Wage Access', 'Loan'] as const;

const SHORT_NAME: Record<string, string> = {
  Checking: 'Spending',
  Savings: 'Savings',
  'Earned Wage Access': 'Cash Advance',
  Debt: 'Cash Advance',
  Loan: 'Kashable loans',
};

const DESIGN_ALL_THREE_BODY =
  "Spending, Savings, and Cash Advance are temporarily unavailable due to a data sync issue with Brightside's core banking system.";

function shortName(type: string): string {
  return SHORT_NAME[type] ?? type;
}

function isBrightsideType(type: string): boolean {
  return (
    type === 'Checking' ||
    type === 'Savings' ||
    type === 'Earned Wage Access' ||
    type === 'Debt'
  );
}

function joinList(names: string[]): string {
  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function uniqueShortNames(types: string[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const type of types) {
    const name = shortName(type);
    if (seen.has(name)) {
      continue;
    }
    seen.add(name);
    names.push(name);
  }
  return names;
}

function orderedTypes(errors: AccountInventoryError[]): string[] {
  const seen = new Set<string>();
  const types: string[] = [];
  for (const err of errors) {
    if (typeof err?.type !== 'string' || !err.type || seen.has(err.type)) {
      continue;
    }
    seen.add(err.type);
    types.push(err.type);
  }
  types.sort((a, b) => {
    const ia = TYPE_ORDER.indexOf(a as (typeof TYPE_ORDER)[number]);
    const ib = TYPE_ORDER.indexOf(b as (typeof TYPE_ORDER)[number]);
    const oa = ia === -1 ? TYPE_ORDER.length : ia;
    const ob = ib === -1 ? TYPE_ORDER.length : ib;
    return oa - ob;
  });
  return types;
}

/**
 * Compose one partial-load banner from AIM `payload.errors`.
 * Never uses `errors[].message` (samples send `"failure"`).
 */
export function mapAccountsBanner(
  errors: AccountInventoryError[] | null | undefined
): AccountsBanner | null {
  if (!errors?.length) {
    return null;
  }
  const types = orderedTypes(errors);
  if (types.length === 0) {
    return null;
  }

  const names = uniqueShortNames(types);
  const list = joinList(names);
  const brightsideOnly = types.every(isBrightsideType);
  const loanOnly = types.length === 1 && types[0] === 'Loan';
  const allThreeBrightside =
    brightsideOnly && DESIGN_BRIGHTSIDE_SHORT_NAMES.every((name) => names.includes(name));

  if (brightsideOnly) {
    const verb = names.length === 1 ? 'is' : 'are';
    return {
      headline: "We're unable to load Brightside account details right now",
      body: allThreeBrightside
        ? DESIGN_ALL_THREE_BODY
        : `${list} ${verb} temporarily unavailable due to a data sync issue with Brightside's core banking system.`,
    };
  }

  if (loanOnly) {
    return {
      headline: "We're unable to load loan details right now",
      body: 'Kashable loans are temporarily unavailable due to a data sync issue with our loan partner.',
    };
  }

  const verb = names.length === 1 ? 'is' : 'are';
  return {
    headline: "We're unable to load some account details right now",
    body: `${list} ${verb} temporarily unavailable due to a data sync issue.`,
  };
}
